/**
 * Live mode, drawn with the design-system screens. Mock and replay keep the original shell.
 * Fixture repos and people stay in the gallery; this module only passes what the account actually has.
 */
import type { DeployRecord, Session } from "@botallion/contracts";
import {
  flowShell,
  html,
  onboardConnect,
  onboardRepo,
  onboardSpec,
  planScreen,
  repoFilesScreen,
  repoHistoryScreen,
  runScreen,
  type WorkPackage as UiPackage,
} from "@botallion/ui";
import type { HomeState } from "./start.ts";
import { answersFor, pendingPlans, pendingPromotions, unansweredBlocking } from "./views/approvals.ts";
import { currentPlan, type DashboardState } from "./state.ts";

export type DesignSection = "runs" | "files" | "history" | "agents";
export type DesignStep = "auto" | "connect" | "spec";

export interface DesignAccount {
  name: string | null;
  email: string | null;
  orgs: { org: string }[];
}

export interface DesignModel {
  me: DesignAccount | null;
  /** Set when the account service could not be reached. */
  unavailable: string | null;
  repo: { org: string; repo: string } | null;
  home: HomeState;
  repoMode: "create" | "import";
  repoName: string;
  spec: string;
  planning: boolean;
  step: DesignStep;
  section: DesignSection;
  command: string;
  sessions: readonly Session[];
  state: DashboardState;
  answers: Record<string, string>;
  busy: ReadonlySet<string>;
  canApprove: boolean;
  error: string | null;
}

const HREFS = { runs: "#design-runs", files: "#design-files", history: "#design-history", agents: "#design-agents" } as const;

function firstName(me: DesignAccount): string {
  const shown = (me.name || me.email || "You").trim();
  return shown.split(/\s+/)[0] || "You";
}

function initials(me: DesignAccount): string {
  const shown = (me.name || me.email || "?").trim();
  const parts = shown.split(/\s+/).filter(Boolean);
  const letters = parts.length > 1 ? `${parts[0]?.[0] ?? ""}${parts.at(-1)?.[0] ?? ""}` : shown.slice(0, 2);
  return letters.toUpperCase() || "?";
}

function placeFor(model: DesignModel, specId?: string) {
  const org = model.repo?.org ?? model.home.orgs[0] ?? model.me?.orgs[0]?.org ?? "";
  const name = model.repo?.repo ?? model.repoName;
  const rows = model.home.repos ?? (model.repo ? [{ org: model.repo.org, name: model.repo.repo }] : []);
  return {
    repo: { org, name, branch: "main", url: "" },
    user: {
      firstName: model.me ? firstName(model.me) : "You",
      initials: model.me ? initials(model.me) : "?",
      settingsHref: "/settings",
    },
    repos: rows.map((r) => ({
      name: r.name,
      href: `?mode=live&org=${encodeURIComponent(r.org)}&repo=${encodeURIComponent(r.name)}`,
      active: model.repo?.org === r.org && model.repo.repo === r.name,
    })),
    hrefs: HREFS,
    ...(specId ? { specId } : {}),
  };
}

function toUiPackages(packages: ReturnType<typeof currentPlan>["packages"]): UiPackage[] {
  const nOf = new Map(packages.map((p, i) => [p.id, i + 1]));
  return packages.map((p, i) => ({
    n: i + 1,
    title: p.title,
    spec: p.specAnchors?.[0]?.heading ?? p.id,
    ground: p.scope.paths.length ? p.scope.paths : [p.id],
    agent: i + 1,
    after: p.dependsOn.flatMap((id) => {
      const n = nOf.get(id);
      return n !== undefined && n !== i + 1 ? [n] : [];
    }),
  }));
}

/** Sign-in itself is the accounts page. This is only the rare case where that check could not be made. */
function accountUnreachable(problem: string) {
  return flowShell({
    children: html`<div class="flow__body">
      <div class="panel">
        <p role="alert">${problem}</p>
        <a class="bt-btn bt-btn--secondary bt-btn--lg" href="/sign-in">Sign in</a>
      </div>
    </div>`,
  });
}

function connect(model: DesignModel) {
  const agents = model.sessions.map((s, i) => ({ index: i + 1, tool: s.vendor ?? "Agent", host: s.agentName }));
  return onboardConnect({
    state: agents.length ? "populated" : "empty",
    agents,
    command: model.command,
    place: placeFor(model),
  });
}

function promotions(deploys: readonly DeployRecord[]) {
  if (!deploys.length) return html``;
  return html`<div class="design-promote">${deploys.map((d) => html`<span class="bt-muted">${d.stack} · ${d.branch}</span>${d.urls.map((u) => html` <a href="${u.url}">${u.url}</a>`)}<button class="bt-btn bt-btn--glow bt-btn--lg" type="button" data-action="approve-promotion" data-deploy="${d.id}">Approve promotion</button>`)}</div>`;
}

/** The live page, as one HTML string. Desktop only, so each approval button exists once. */
export function designMarkup(model: DesignModel): string {
  if (!model.me) return model.unavailable ? accountUnreachable(model.unavailable).value : "";
  const place = placeFor(model);
  let screen;
  if (!model.repo) {
    screen = onboardRepo({
      mode: model.repoMode,
      name: model.repoName,
      url: "",
      offerDemo: true,
      existing: model.home.repos ?? [],
      orgs: model.home.orgs,
      error: model.home.error ?? model.error,
      busy: model.home.creating,
      place,
    });
  } else if (model.section === "files") {
    screen = repoFilesScreen({ state: "empty", place });
  } else if (model.section === "history") {
    screen = repoHistoryScreen({ state: "empty", place });
  } else if (model.section === "agents") {
    screen = connect(model);
  } else {
    const pending = pendingPlans(model.state);
    const plan = pending[0] ? currentPlan(model.state, pending[0]) : null;
    if (model.planning || plan) {
      const packages = plan ? toUiPackages(plan.packages) : [];
      const blocked = plan ? unansweredBlocking({ answers: model.answers }, plan).length > 0 : false;
      screen = planScreen({
        state: model.planning && !plan ? "loading" : packages.length ? "populated" : "empty",
        device: "desktop",
        packages,
        questions: plan
          ? plan.questions.filter((q) => q.blocking || !q.answer).map((q) => ({
              id: q.id,
              text: q.text,
              value: answersFor({ answers: model.answers }, plan)[q.id] ?? model.answers[`${plan.specId}/${q.id}`] ?? q.answer ?? "",
            }))
          : [],
        approveDisabled: !plan || !model.canApprove || blocked || model.busy.has(`plan:${plan.specId}`),
        place: placeFor(model, plan?.specId),
      });
    } else if (model.step === "spec") {
      screen = onboardSpec({ text: model.spec, agentsReady: model.sessions.length, place });
    } else if (model.step === "connect" || !Object.values(model.state.plans).some((p) => p.approvedBy)) {
      screen = connect(model);
    } else {
      screen = runScreen({ state: "empty", device: "desktop", place });
    }
  }
  const waiting = model.section === "runs" || model.section === "agents" ? pendingPromotions(model.state) : [];
  return `${screen.value}${promotions(waiting).value}`;
}
